import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  memo,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PartnerLayout from '../../components/layout/PartnerLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  ArrowLeft,
  FileText,
  Clock,
  CheckCircle2,
  MessageSquare,
  User,
  UserCog,
  ListFilter,
  Loader2,
  DollarSign,
  Upload,
  Send,
  CheckCircle,
  Activity,
  Circle,
  CircleDot,
  AlertTriangle,
  History,
} from 'lucide-react';
import { partnerAPI } from '../../services/api';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import ServiceChat from '../../components/services/ServiceChat';
import { PARTNER_SERVICE_CATALOG } from '../../components/partner/PartnerApplyServiceFlow';
import {
  TrackingSkeleton,
  ErrorState,
  EmptyState,
} from '../../components/partner/PartnerStateViews';

// ── Status + task config ────────────────────────────────────────────────────

const STATUS_CONFIG = {
  PENDING_ADMIN_ASSIGNMENT: { label: 'Pending Assignment', color: 'bg-yellow-100 text-yellow-700' },
  ASSIGNED: { label: 'Assigned', color: 'bg-blue-100 text-blue-700' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-indigo-100 text-indigo-700' },
  WAITING_STUDENT: { label: 'Waiting on Partner', color: 'bg-orange-100 text-orange-700' },
  ON_HOLD: { label: 'On Hold', color: 'bg-gray-100 text-gray-700' },
  COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

const TASK_STATUS_COLORS = {
  PENDING: 'bg-amber-100 text-amber-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  SUBMITTED: 'bg-indigo-100 text-indigo-700',
  COMPLETED: 'bg-green-100 text-green-700',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
};

const TASK_SUBMITTABLE = new Set(['PENDING', 'IN_PROGRESS', 'REJECTED']);

// Canonical lifecycle order for the horizontal timeline
const TIMELINE_STAGES = [
  { key: 'applied', label: 'Applied', statuses: ['PENDING_ADMIN_ASSIGNMENT'] },
  { key: 'assigned', label: 'Assigned', statuses: ['ASSIGNED'] },
  { key: 'in_progress', label: 'In Progress', statuses: ['IN_PROGRESS', 'WAITING_STUDENT', 'ON_HOLD'] },
  { key: 'review', label: 'Review', statuses: ['REVIEW'] },
  { key: 'completed', label: 'Completed', statuses: ['COMPLETED'] },
];

const stageIndexFor = (status) => {
  const idx = TIMELINE_STAGES.findIndex((s) => s.statuses.includes(status));
  if (idx >= 0) return idx;
  if (status === 'CANCELLED') return -1;
  return 0;
};

// ── Memoized row components ────────────────────────────────────────────────

const TaskRow = memo(function TaskRow({ task, canInteract, onSubmit }) {
  const status = task.status || 'PENDING';
  const canSubmit = canInteract && TASK_SUBMITTABLE.has(status);
  return (
    <Card className="partner-card rounded-[24px] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="text-sm font-medium text-slate-900">{task.title}</p>
            <Badge
              className={`text-xs ${
                TASK_STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'
              }`}
            >
              {status.replace(/_/g, ' ')}
            </Badge>
            {!canInteract && (
              <span className="text-[10px] text-gray-400 italic">
                locked — case not active
              </span>
            )}
          </div>
          {task.description && (
            <p className="line-clamp-2 text-sm text-slate-600">
              {task.description}
            </p>
          )}
          {task.dueDate && (
            <p className="mt-1 text-xs text-slate-500">
              Due {format(new Date(task.dueDate), 'MMM dd, yyyy')}
            </p>
          )}
        </div>
        <Button
          size="sm"
          disabled={!canSubmit}
          onClick={() => onSubmit(task)}
          className={
            canSubmit
              ? 'partner-primary-btn rounded-2xl'
              : 'cursor-not-allowed rounded-2xl bg-white/45 text-slate-400'
          }
        >
          <Upload className="w-3.5 h-3.5 mr-1" />
          Submit
        </Button>
      </div>
    </Card>
  );
});

const TimelineBar = memo(function TimelineBar({ status }) {
  const currentIdx = stageIndexFor(status);
  const cancelled = status === 'CANCELLED';
  return (
    <Card className="partner-card rounded-[28px] p-5">
      <div className="flex items-center justify-between mb-4">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Activity className="h-4 w-4 text-[#8D68E8]" />
          Service Lifecycle
        </h4>
        {cancelled && (
          <Badge className="border-[#A87DFA]/20 bg-[#A87DFA]/12 text-[#6D49D4]">
            <AlertTriangle className="w-3 h-3 mr-1" /> Cancelled
          </Badge>
        )}
      </div>
      <div className="flex items-center">
        {TIMELINE_STAGES.map((stage, i) => {
          const done = !cancelled && i < currentIdx;
          const active = !cancelled && i === currentIdx;
          return (
            <React.Fragment key={stage.key}>
              <div className="flex flex-col items-center flex-shrink-0 w-20">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    done
                      ? 'bg-[#A87DFA] text-white'
                      : active
                      ? 'bg-[#A87DFA] text-white ring-4 ring-[#A87DFA]/12'
                      : 'bg-white/55 text-slate-400'
                  }`}
                >
                  {done ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : active ? (
                    <CircleDot className="w-4 h-4" />
                  ) : (
                    <Circle className="w-4 h-4" />
                  )}
                </div>
                <span
                  className={`text-[11px] mt-1.5 text-center ${
                    active
                      ? 'font-semibold text-[#6D49D4]'
                      : done
                      ? 'text-[#6D49D4]'
                      : 'text-slate-400'
                  }`}
                >
                  {stage.label}
                </span>
              </div>
              {i < TIMELINE_STAGES.length - 1 && (
                <div
                  className={`flex-1 h-0.5 ${
                    done ? 'bg-[#A87DFA]' : 'bg-[#A87DFA]/16'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </Card>
  );
});

// ── Main page ──────────────────────────────────────────────────────────────

export default function PartnerServiceTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState('overview');
  const [payments, setPayments] = useState([]);

  // Task submit dialog
  const [submitTask, setSubmitTask] = useState(null);
  const [submitText, setSubmitText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Payment request dialog
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);

  const loadPayments = useCallback(async () => {
    try {
      const res = await partnerAPI.listPaymentRequests({ serviceRequestId: id });
      setPayments(res.data || []);
    } catch {
      setPayments([]);
    }
  }, [id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await partnerAPI.getServiceRequestById(id);
      // Backend returns { serviceRequest, tasks, counselor, student, partner }.
      // Merge siblings onto the serviceRequest so downstream code can read
      // request.counselor / request.student / request.partner reliably.
      const sr = res.serviceRequest || res.data || {};
      const merged = {
        ...sr,
        counselor: sr.counselor || res.counselor || null,
        student: sr.student || res.student || null,
        partner: sr.partner || res.partner || null,
      };
      setRequest(merged);

      // Prefer tasks from the detail response (already filtered by SR id);
      // fall back to /partners/tasks?serviceRequestId= for backwards compat.
      const detailTasks = res.tasks || sr.tasks;
      if (Array.isArray(detailTasks) && detailTasks.length >= 0) {
        setTasks(detailTasks);
        await loadPayments();
      } else {
        const [tRes] = await Promise.all([
          partnerAPI.getTasks({ serviceRequestId: id }).catch(() => ({ data: [] })),
          loadPayments(),
        ]);
        setTasks(tRes.data || tRes.tasks || []);
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Unable to load this service request'
      );
    } finally {
      setLoading(false);
    }
  }, [id, loadPayments]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Derived state (memoized) ──────────────────────────────────────────

  const serviceDef = useMemo(
    () =>
      PARTNER_SERVICE_CATALOG.find(
        (s) => s.serviceType === request?.serviceType
      ) || {},
    [request?.serviceType]
  );

  const statusConf = useMemo(
    () =>
      STATUS_CONFIG[request?.status] || STATUS_CONFIG.PENDING_ADMIN_ASSIGNMENT,
    [request?.status]
  );

  const canInteract = useMemo(() => {
    const s = request?.status;
    return s && !['COMPLETED', 'CANCELLED', 'PENDING_ADMIN_ASSIGNMENT'].includes(s);
  }, [request?.status]);

  const isCompleted = request?.status === 'COMPLETED';

  const hasActivePayment = useMemo(
    () => payments.some((p) => ['pending', 'approved', 'paid'].includes(p.status)),
    [payments]
  );
  const canRequestPayment = isCompleted && !hasActivePayment;

  // Merge statusHistory + task events into one chronological activity log
  const activityLog = useMemo(() => {
    const entries = [];
    (request?.statusHistory || []).forEach((h, i) => {
      entries.push({
        id: `status_${i}`,
        type: 'status',
        at: h.changedAt || h.timestamp || h.createdAt,
        label: `Status changed to ${(h.status || '').replace(/_/g, ' ')}`,
        by: h.changedBy || h.changedByRole,
        note: h.note,
      });
    });
    tasks.forEach((t) => {
      if (t.createdAt) {
        entries.push({
          id: `task_created_${t.taskId || t._id}`,
          type: 'task',
          at: t.createdAt,
          label: `Task created: ${t.title}`,
          by: t.createdByRole || 'counselor',
        });
      }
      if (t.submittedAt) {
        entries.push({
          id: `task_submitted_${t.taskId || t._id}`,
          type: 'task',
          at: t.submittedAt,
          label: `Task submitted: ${t.title}`,
          by: 'partner',
        });
      }
      if (t.reviewedAt) {
        entries.push({
          id: `task_reviewed_${t.taskId || t._id}`,
          type: 'task',
          at: t.reviewedAt,
          label: `Task ${t.status?.toLowerCase() || 'reviewed'}: ${t.title}`,
          by: 'counselor',
        });
      }
    });
    payments.forEach((p) => {
      entries.push({
        id: `payment_${p.paymentRequestId}`,
        type: 'payment',
        at: p.createdAt,
        label: `Payment request submitted: $${Number(p.amount).toFixed(2)}`,
        by: 'partner',
        note: p.note,
      });
      if (p.approvedAt) {
        entries.push({
          id: `payment_approved_${p.paymentRequestId}`,
          type: 'payment',
          at: p.approvedAt,
          label: `Payment request approved`,
          by: 'admin',
        });
      }
      if (p.paidAt) {
        entries.push({
          id: `payment_paid_${p.paymentRequestId}`,
          type: 'payment',
          at: p.paidAt,
          label: `Payment marked as paid${p.paymentReference ? ` (ref: ${p.paymentReference})` : ''}`,
          by: 'admin',
        });
      }
    });
    return entries
      .filter((e) => e.at)
      .sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [request?.statusHistory, tasks, payments]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleTaskSubmit = useCallback(async () => {
    if (!submitText.trim()) {
      toast.error('Please enter a response');
      return;
    }
    if (!canInteract) {
      toast.error('Cannot submit in the current case state');
      return;
    }
    setSubmitting(true);
    try {
      await partnerAPI.submitTask(
        submitTask.taskId || submitTask._id,
        submitText,
        []
      );
      toast.success('Task submitted on behalf of student');
      setSubmitTask(null);
      setSubmitText('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit task');
    } finally {
      setSubmitting(false);
    }
  }, [submitText, submitTask, canInteract, load]);

  const handlePaymentRequest = useCallback(async () => {
    const amt = parseFloat(paymentAmount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (!isCompleted) {
      toast.error('Payment can only be requested after service completion');
      return;
    }
    setPaymentSubmitting(true);
    try {
      await partnerAPI.createPaymentRequest({
        serviceRequestId: id,
        amount: amt,
        note: paymentNote,
      });
      toast.success('Payment request submitted for admin approval');
      setPaymentOpen(false);
      setPaymentAmount('');
      setPaymentNote('');
      loadPayments();
    } catch (err) {
      toast.error(
        err.response?.data?.error || 'Failed to submit payment request'
      );
    } finally {
      setPaymentSubmitting(false);
    }
  }, [paymentAmount, paymentNote, isCompleted, id, loadPayments]);

  const openSubmitDialog = useCallback((t) => setSubmitTask(t), []);

  // ── Render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <PartnerLayout>
        <TrackingSkeleton />
      </PartnerLayout>
    );
  }

  if (error || !request) {
    return (
      <PartnerLayout>
        <div className="space-y-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/partner/service-requests')}
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <ErrorState
            title={error ? 'Failed to load request' : 'Service request not found'}
            message={error}
            onRetry={load}
          />
        </div>
      </PartnerLayout>
    );
  }

  const student = request.student?.user || request.student || {};
  const counselor = request.counselor || request.assignedCounselor;
  const progress = request.progress || 0;
  const ServiceIcon = serviceDef.icon || FileText;

  return (
    <PartnerLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <Button
              variant="outline"
              size="sm"
              className="partner-secondary-btn rounded-2xl"
              onClick={() => navigate('/partner/service-requests')}
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#A87DFA]/16 bg-[linear-gradient(135deg,rgba(168,125,250,0.18),rgba(168,125,250,0.06))]">
                  <ServiceIcon
                    className="w-5 h-5 text-[#7A55DF]"
                  />
                </div>
                <h1 className="text-2xl font-bold text-slate-900">
                  {serviceDef.label || request.serviceType?.replace(/_/g, ' ')}
                </h1>
                <Badge className={statusConf.color}>{statusConf.label}</Badge>
              </div>
              <p className="mt-1 text-sm text-slate-500">
                Request ID: {request.serviceRequestId || request._id}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="partner-secondary-btn rounded-2xl" onClick={load}>
              <Clock className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button
              onClick={() => setPaymentOpen(true)}
              disabled={!canRequestPayment}
              title={
                !isCompleted
                  ? 'Available once the service is completed'
                  : hasActivePayment
                  ? 'A payment request already exists for this service'
                  : 'Request payment'
              }
              className={
                canRequestPayment
                  ? 'partner-primary-btn rounded-2xl'
                  : 'cursor-not-allowed rounded-2xl bg-white/45 text-slate-400'
              }
            >
              <DollarSign className="w-4 h-4 mr-1" /> Request Payment
            </Button>
          </div>
        </div>

        {/* Progress bar */}
        <Card className="partner-card rounded-[28px] p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              Overall Progress
            </span>
            <span className="text-sm font-semibold text-[#6D49D4]">
              {progress}%
            </span>
          </div>
          <Progress value={progress} className="h-2 bg-[#A87DFA]/12" />
        </Card>

        {/* Canonical status lifecycle */}
        <TimelineBar status={request.status} />

        {/* Interaction-mode guard banner */}
        <div className="partner-card rounded-[24px] px-4 py-3">
          <div className="flex items-center gap-2">
          <UserCog className="w-4 h-4 text-[#8D68E8]" />
          <p className="text-xs text-[#6D49D4]">
            Partner-managed case — you act on behalf of the student. The student
            is not required to interact with this service directly.
          </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-[20px] border border-[#A87DFA]/16 bg-white/45 p-1">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="tasks">
              Tasks {tasks.length > 0 && `(${tasks.length})`}
            </TabsTrigger>
            <TabsTrigger value="activity">
              <History className="w-3.5 h-3.5 mr-1" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="payments">
              <DollarSign className="w-3.5 h-3.5 mr-1" />
              Payments {payments.length > 0 && `(${payments.length})`}
            </TabsTrigger>
            <TabsTrigger value="chat">
              <MessageSquare className="w-3.5 h-3.5 mr-1" />
              Chat
            </TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="partner-card rounded-[26px] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-[#8D68E8]" />
                  <h4 className="font-semibold text-slate-900">Student</h4>
                </div>
                {student.firstName ? (
                  <>
                    <p className="text-sm font-medium text-slate-900">
                      {student.firstName} {student.lastName}
                    </p>
                    {student.email && (
                      <p className="text-xs text-slate-500">{student.email}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm italic text-slate-500">
                    Student details unavailable
                  </p>
                )}
              </Card>

              <Card className="partner-card rounded-[26px] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <UserCog className="w-4 h-4 text-[#8D68E8]" />
                  <h4 className="font-semibold text-slate-900">Counselor</h4>
                </div>
                {counselor ? (
                  <>
                    <p className="text-sm font-medium text-slate-900">
                      {counselor.firstName} {counselor.lastName}
                    </p>
                    {counselor.email && (
                      <p className="text-xs text-slate-500">{counselor.email}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm italic text-slate-500">
                    Awaiting counselor assignment
                  </p>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Tasks — strict state control */}
          <TabsContent value="tasks" className="mt-4">
            {tasks.length === 0 ? (
              <EmptyState
                icon={ListFilter}
                title="No tasks yet"
                description={
                  canInteract
                    ? 'Tasks assigned by the counselor will appear here'
                    : 'Tasks become available once a counselor is assigned'
                }
              />
            ) : (
              <div className="space-y-3">
                {!canInteract && (
                  <div className="flex items-center gap-2 rounded-[20px] border border-[#A87DFA]/16 bg-white/40 px-3 py-2 text-xs text-[#6D49D4]">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {request.status === 'COMPLETED'
                      ? 'Case is complete — tasks are locked.'
                      : request.status === 'PENDING_ADMIN_ASSIGNMENT'
                      ? 'Task actions are disabled until a counselor is assigned.'
                      : 'Task actions are disabled in the current state.'}
                  </div>
                )}
                {tasks.map((t) => (
                  <TaskRow
                    key={t.taskId || t._id}
                    task={t}
                    canInteract={canInteract}
                    onSubmit={openSubmitDialog}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Activity log */}
          <TabsContent value="activity" className="mt-4">
            {activityLog.length === 0 ? (
              <EmptyState
                icon={History}
                title="No activity yet"
                description="Status changes, task updates and counselor actions will be logged here."
              />
            ) : (
              <Card className="partner-card rounded-[28px] p-5">
                <div className="space-y-3">
                  {activityLog.map((e) => (
                    <div key={e.id} className="flex items-start gap-3">
                      <div
                        className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                          e.type === 'status'
                            ? 'bg-purple-500'
                            : e.type === 'payment'
                            ? 'bg-green-500'
                            : 'bg-blue-500'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900">{e.label}</p>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                          <span>
                            {formatDistanceToNow(new Date(e.at), {
                              addSuffix: true,
                            })}
                          </span>
                          {e.by && <span>· {String(e.by)}</span>}
                        </div>
                        {e.note && (
                          <p className="mt-1 text-xs italic text-slate-600">
                            {e.note}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Payments */}
          <TabsContent value="payments" className="mt-4">
            {payments.length === 0 ? (
              <EmptyState
                icon={DollarSign}
                title="No payment requests"
                description={
                  isCompleted
                    ? 'Click "Request Payment" to record one.'
                    : 'Payment requests are available once the service is completed.'
                }
              />
            ) : (
              <div className="space-y-3">
                {payments.map((p) => {
                  const badgeCls =
                    p.status === 'paid'
                      ? 'bg-green-100 text-green-700'
                      : p.status === 'approved'
                      ? 'bg-blue-100 text-blue-700'
                      : p.status === 'rejected' || p.status === 'cancelled'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-700';
                  return (
                    <Card key={p.paymentRequestId} className="partner-card rounded-[24px] p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-900">
                            ${Number(p.amount).toFixed(2)}
                          </p>
                          {p.note && (
                            <p className="mt-0.5 text-xs text-slate-600">{p.note}</p>
                          )}
                          <p className="mt-1 text-xs text-slate-500">
                            Requested {formatDistanceToNow(new Date(p.createdAt), { addSuffix: true })}
                            {p.paidAt && ` · paid ${formatDistanceToNow(new Date(p.paidAt), { addSuffix: true })}`}
                            {p.paymentReference && ` · ref: ${p.paymentReference}`}
                          </p>
                        </div>
                        <Badge className={badgeCls}>{p.status}</Badge>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Chat */}
          <TabsContent value="chat" className="mt-4">
            {request.status === 'PENDING_ADMIN_ASSIGNMENT' ? (
              <EmptyState
                icon={MessageSquare}
                title="Chat not available yet"
                description="Chat opens once a counselor is assigned to this case."
              />
            ) : (
              <Card className="partner-card overflow-hidden rounded-[28px] p-0" style={{ height: 600 }}>
                <ServiceChat
                  serviceRequestId={request.serviceRequestId || request._id}
                  isStaffView={true}
                />
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Task Submit Dialog */}
      <Dialog open={!!submitTask} onOpenChange={(v) => !v && setSubmitTask(null)}>
        <DialogContent className="partner-dialog max-w-md rounded-[28px] p-0 flex flex-col max-h-[85vh]">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
            <DialogTitle>Submit Task (on behalf of student)</DialogTitle>
            <DialogDescription>{submitTask?.title}</DialogDescription>
          </DialogHeader>
          <div className="px-6 py-2 overflow-y-auto flex-1">
            <Textarea
              placeholder="Describe how this task was completed…"
              value={submitText}
              onChange={(e) => setSubmitText(e.target.value)}
              rows={5}
            />
          </div>
          <DialogFooter className="px-6 py-4 border-t shrink-0 bg-white rounded-b-[28px]">
            <Button variant="outline" onClick={() => setSubmitTask(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleTaskSubmit}
              disabled={submitting || !submitText.trim()}
              className="partner-primary-btn rounded-2xl"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Request Dialog */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="partner-dialog max-w-md rounded-[28px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Request Payment
            </DialogTitle>
            <DialogDescription>
              Service is completed. Record the payment amount due from the student
              for this case — counselor and admin will be notified via chat.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">
                Amount (USD)
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 499.00"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">
                Note (optional)
              </label>
              <Textarea
                placeholder="Any context for the counselor or admin…"
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePaymentRequest}
              disabled={paymentSubmitting || !paymentAmount}
              className="partner-primary-btn rounded-2xl"
            >
              {paymentSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Record Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PartnerLayout>
  );
}
